/**
 * ============================================================================
 * useKbImport.ts — Hooks de importação em lote de arquivos para a KB
 * ----------------------------------------------------------------------------
 * F3 · Importadores (Markdown / PDF)
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 — Versão inicial.
 *
 * Fluxo:
 *   1) Faz upload dos arquivos para o bucket privado `kb-imports`
 *      (caminho: <tenant_id>/<batch_id>/<filename>).
 *   2) Invoca a edge function `kb-import-md` ou `kb-import-pdf` que
 *      processa os arquivos e cria as páginas draft na KB.
 * ============================================================================
 */
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/lib/logger";
import { toast } from "@/hooks/use-toast";

// Tipos de retorno da edge function
export interface ImportResult {
  created_count: number;
  skipped_count: number;
  failed_count: number;
  created: Array<{ filename: string; page_id: string; action: string; slug_used: string; pages?: number }>;
  skipped: Array<{ filename: string; reason: string }>;
  failed: Array<{ filename: string; reason: string }>;
}

export interface ImportParams {
  space_id: string;
  collection_id?: string | null;
  data_classification: "public" | "internal" | "confidential" | "restricted";
  ai_visibility: "public" | "tenant" | "none" | "inherit";
  files: File[];
  /** Callback opcional de progresso (arquivos enviados / total). */
  onProgress?: (uploaded: number, total: number) => void;
}

/**
 * Sobe os arquivos para o bucket kb-imports e retorna os storage_paths.
 * Faz upload sequencial simples (lote pequeno, fluxo previsível).
 */
async function uploadFiles(
  tenantId: string,
  files: File[],
  onProgress?: (uploaded: number, total: number) => void,
): Promise<Array<{ storage_path: string; filename: string; size: number }>> {
  // Gera um batch_id local (UUID v4 simples via crypto)
  const batchId = crypto.randomUUID();
  const out: Array<{ storage_path: string; filename: string; size: number }> = [];
  let uploaded = 0;

  for (const file of files) {
    // Sanitiza filename para evitar caracteres problemáticos no storage
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
    const path = `${tenantId}/${batchId}/${safeName}`;
    const { error } = await supabase.storage
      .from("kb-imports")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (error) {
      logger.error("useKbImport", "upload_failed", { file: file.name, error });
      throw new Error(`Falha ao enviar ${file.name}: ${error.message}`);
    }
    out.push({ storage_path: path, filename: file.name, size: file.size });
    uploaded++;
    onProgress?.(uploaded, files.length);
  }
  return out;
}

/** Importa arquivos Markdown (1 arquivo = 1 página draft). */
export function useKbImportMarkdown() {
  const { currentTenant } = useAuth();
  return useMutation<ImportResult, Error, ImportParams>({
    mutationFn: async (params) => {
      if (!currentTenant?.id) throw new Error("Tenant ativo não encontrado");
      const uploaded = await uploadFiles(currentTenant.id, params.files, params.onProgress);
      const { data, error } = await supabase.functions.invoke("kb-import-md", {
        body: {
          space_id: params.space_id,
          collection_id: params.collection_id ?? null,
          data_classification: params.data_classification,
          ai_visibility: params.ai_visibility,
          files: uploaded,
        },
      });
      if (error) throw error;
      return data as ImportResult;
    },
    onError: (err) => {
      logger.error("useKbImportMarkdown", "erro", err);
      toast({ title: "Erro ao importar Markdown", description: err.message, variant: "destructive" });
    },
  });
}

/** Importa PDFs (1 arquivo = 1 página draft). */
export function useKbImportPdf() {
  const { currentTenant } = useAuth();
  return useMutation<ImportResult, Error, ImportParams>({
    mutationFn: async (params) => {
      if (!currentTenant?.id) throw new Error("Tenant ativo não encontrado");
      const uploaded = await uploadFiles(currentTenant.id, params.files, params.onProgress);
      const { data, error } = await supabase.functions.invoke("kb-import-pdf", {
        body: {
          space_id: params.space_id,
          collection_id: params.collection_id ?? null,
          data_classification: params.data_classification,
          ai_visibility: params.ai_visibility,
          files: uploaded,
        },
      });
      if (error) throw error;
      return data as ImportResult;
    },
    onError: (err) => {
      logger.error("useKbImportPdf", "erro", err);
      toast({ title: "Erro ao importar PDF", description: err.message, variant: "destructive" });
    },
  });
}
